import { useDispatch } from 'react-redux';
import './css/app.css';
import './css/main.css';
import './css/common.css';
import './css/payment.css';
import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { change_Current_Location, changeHeaderTitle } from '../store';
import { LoadingModal } from './component/modal/modal';
import useWindowSize from './hook/useWindowSize';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGift } from "@fortawesome/free-solid-svg-icons";
import { MessageCircleMore, Heart } from 'lucide-react';
import axios from 'axios';

function UrlDetail() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    useWindowSize(); // Rwidth, Rheight 사용 안 하면 호출만 유지

    const { mid: rawMid } = useParams();
    const mid = rawMid ? decodeURIComponent(rawMid) : "";
    const { state: passed } = useLocation();

    const [loading, setLoading] = useState(true);

    const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('ko-KR') : n);
    const num = (v) => (v == null ? 0 : Number(v));
    const toFull = (u) => {
        if (!u) return '';
        if (/^https?:\/\//i.test(u)) return u;
        return `${process.env.REACT_APP_API}${u}`;
    };
    const priceSuffix = (v) => {
        const n = Number(v || 0);
        if (!n) return '';
        const sign = n > 0 ? '+' : '-';
        const abs = Math.abs(n).toLocaleString('ko-KR');
        return `(${sign}${abs}원)`;
    };

    const [product, setProduct] = useState({
        title: '',
        brand: '',
        modelName: '',
        origin: '',
        listPrice: 0,     // 정상가 (price)
        salePrice: 0,     // 최종가 (is_discount_active ? discount_price : price)
        unitPriceText: '',
        freeShipping: false,
        shippingFee: 0,
        deliveryText: '',
        benefitText: '',
        heroImages: [],
        detailImages: [],
    });

    const [optionDefs, setOptionDefs] = useState([]); // [{id,name,stockQuantity,additionalPrice,isActive}]
    const [optQty, setOptQty] = useState({});

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [entryMode, setEntryMode] = useState('buy');
    const [liked, setLiked] = useState(false);

    useEffect(() => {
        if (sheetOpen) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => (document.body.style.overflow = prev);
        }
    }, [sheetOpen]);

    useEffect(() => {
        dispatch(change_Current_Location('URL상품상세'));
        dispatch(changeHeaderTitle({ mainTitle: 'URL상품상세', subTitle: '' }));
    }, [dispatch]);

    // ===== API 로딩 & 매핑 =====
    useEffect(() => {
        if (!mid) return;
        (async () => {
            try {
                const { data } = await axios.get(
                    `${process.env.REACT_APP_API}/api/products/${mid}/${passed?.product_id}`,
                    { withCredentials: true }
                );
                console.log(data)
                if (data?.success && data?.data) {
                    const d = data.data;

                    // ✔ 가격 매핑 수정
                    // price = 정상가, discount_price = 할인된 최종가
                    const normalPrice = num(d.price);
                    const salePrice = d.is_discount_active ? num(d.discount_price) : normalPrice;

                    const freeShipping = String(d.is_free_shipping) === '1';
                    const shippingFee = num(d.shipping_fee);
                    const deliveryText = freeShipping ? '3일 이내 배송 예정' : `배송비 ${fmt(shippingFee)}원`;

                    const mainImg = toFull(d.main_image);
                    const detailImgs = Array.isArray(d.descriptionImages)
                        ? d.descriptionImages
                            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                            .map(it => toFull(it.imageUrl))
                        : [];

                    const opts = Array.isArray(d.options) ? d.options : [];
                    const mappedOptions = opts.map((o, i) => ({
                        id: o.optionId ?? i + 1,
                        name: o.optionName ?? `옵션 ${i + 1}`,
                        stockQuantity: o.stockQuantity ?? 0,
                        isActive: o.isActive ?? 1,
                        additionalPrice: num(o.additionalPrice) || 0,
                        sortOrder: o.sortOrder ?? i + 1,
                    })).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

                    const initQty = {};
                    mappedOptions.forEach(o => { initQty[o.id] = 0; });

                    setProduct({
                        title: d.product_name || '',
                        brand: d.co_nm || '',
                        modelName: d.product_code || '',
                        origin: '',
                        listPrice: normalPrice,  // ← 정상가
                        salePrice,               // ← 최종가
                        unitPriceText: '',
                        freeShipping,
                        shippingFee,
                        deliveryText,
                        benefitText: d.product_description || '',
                        heroImages: mainImg ? [mainImg] : [],
                        detailImages: detailImgs,
                    });

                    setOptionDefs(mappedOptions.length > 0 ? mappedOptions : [{ id: 0, name: '옵션', stockQuantity: 0, additionalPrice: 0 }]);
                    setOptQty(mappedOptions.length > 0 ? initQty : { 0: 0 });
                }
            } catch (e) {
                console.error('[GET /api/products/:mid/:id] failed', e);
            } finally {
                setLoading(false);
            }
        })();
    }, [mid, passed?.product_id]);

    const OPTION_IDS = useMemo(() => optionDefs.map(o => o.id), [optionDefs]);
    const OPTION_LABEL = (id) => optionDefs.find(o => o.id === id)?.name ?? `옵션 ${id}`;
    const unitPrice = product.salePrice;

    const discountRate = useMemo(() => {
        if (!product.listPrice || !product.salePrice) return null;
        if (product.listPrice <= product.salePrice) return null;
        return Math.round((1 - product.salePrice / product.listPrice) * 100);
    }, [product.listPrice, product.salePrice]);

    const selections = useMemo(
        () =>
            OPTION_IDS.filter((id) => (optQty[id] ?? 0) > 0).map((id) => ({
                id: `opt_${id}`,
                idx: id,
                label: OPTION_LABEL(id),
                qty: optQty[id],
            })),
        [OPTION_IDS, optQty]
    );

    const totalQty = useMemo(
        () => OPTION_IDS.reduce((sum, id) => sum + (optQty[id] ?? 0), 0),
        [OPTION_IDS, optQty]
    );
    const totalAmount = useMemo(
        () => OPTION_IDS.reduce((sum, id) => sum + (optQty[id] ?? 0) * unitPrice, 0),
        [OPTION_IDS, optQty, unitPrice]
    );

    const openSheet = (mode) => {
        setEntryMode(mode);
        setSheetOpen(true);
    };

    const chooseOption = (id) => {
        setOptQty((prev) => (prev[id] > 0 ? prev : { ...prev, [id]: 1 }));
    };
    const incQtyItem = (rowId) => {
        const id = Number(rowId.split('_')[1]);
        setOptQty((q) => ({ ...q, [id]: Math.min(99, (q[id] ?? 0) + 1) }));
    };
    const decQtyItem = (rowId) => {
        const id = Number(rowId.split('_')[1]);
        setOptQty((q) => ({ ...q, [id]: Math.max(1, (q[id] ?? 1) - 1) }));
    };
    const removeItem = (rowId) => {
        const id = Number(rowId.split('_')[1]);
        setOptQty((q) => ({ ...q, [id]: 0 }));
    };

    const handleConfirm = (mode) => {
        if (totalQty === 0) return;
        if (!mid) {
            navigate('/urlNotice', { replace: true });
            return;
        }
        const payloadSelections = selections.map((s) => ({
            id: s.id,
            optionIndex: s.idx,
            optionLabel: s.label,
            qty: s.qty,
        }));
        const payload = {
            passed,
            product,
            selections: payloadSelections,
            totalQuantity: totalQty,
            quantity: totalQty,
            unitPrice,
            amount: totalAmount,
            checkoutMode: mode || entryMode,
        };
        setSheetOpen(false);
        navigate(`/${mid}/payment`, { state: payload });
    };

    return (
        <>
            {loading ? <LoadingModal /> : ''}
            <div style={{ background: '#fff', minHeight: '100dvh' }}>
                <div className="urlDetail-wrap">
                    {/* 상단 대표 이미지 */}
                    <section className="urlDetail-hero">
                        {product.heroImages[0] ? (
                            <img className="urlDetail-heroImg" src={product.heroImages[0]} alt={product.title} />
                        ) : (
                            <div className="urlDetail-heroImg" style={{ background: '#efefef' }} />
                        )}
                    </section>

                    {/* 타이틀 */}
                    <section className="urlDetail-titleBox">
                        <h1 className="urlDetail-title">
                            {product.title || '상품명'}
                            {product.origin && <span className="urlDetail-origin"> [원산지:{product.origin}]</span>}
                        </h1>
                    </section>

                    {/* 가격 */}
                    <section className="urlDetail-priceBox">
                        <div className="urlDetail-priceRow urlDetail-priceRow--strike">
                            {discountRate !== null && <strong className="urlDetail-discount">{discountRate}%</strong>}
                            <span className="urlDetail-listPrice">{fmt(product.listPrice)}원</span>
                        </div>
                        <div className="urlDetail-priceRow">
                            <strong className="urlDetail-salePrice">{fmt(product.salePrice)}원</strong>
                        </div>
                        <div className="urlDetail-shipLine">
                            {product.freeShipping ? (
                                <span className="urlDetail-freeShip">무료배송</span>
                            ) : (
                                <span className="urlDetail-freeShip">배송비 {fmt(product.shippingFee)}원</span>
                            )}
                        </div>
                    </section>

                    {/* 혜택 + 배송 */}
                    {(product.benefitText || product.deliveryText) && (
                        <section className="urlDetail-benefitWrap">
                            {/* {product.benefitText && <div className="urlDetail-benefit">{product.benefitText}</div>} */}
                            {product.deliveryText && (
                                <div className="urlDetail-delivery">
                                    <span className="urlDetail-nologo">🚚</span>
                                    {product.deliveryText}
                                </div>
                            )}
                        </section>
                    )}

                    {/* 탭 */}
                    <nav className="urlDetail-tabs" role="tablist" aria-label="상품 탭">
                        <span className="urlDetail-tab is-active" role="tab" aria-selected="true">
                            상세정보
                        </span>
                    </nav>

                    {/* 상세정보 */}
                    <section className={'urlDetail-detailBody ' + (detailsOpen ? 'is-open' : 'is-closed')}>
                        <div className="urlDetail-detailInner">
                            <div className="urlDetail-spec urlDetail-spec--inDetail">
                                <h2 className="urlDetail-specTitle">상품정보</h2>
                                <dl className="urlDetail-specTable">
                                    <dt>이미지 참조</dt>

                                    {/* <dt>브랜드</dt><dd>{product.brand || '-'}</dd>
                                    <dt>모델명</dt><dd>{product.modelName || '-'}</dd> */}
                                </dl>
                            </div>

                            {product.detailImages.map((src, i) => (
                                <img className="urlDetail-detailImg" src={src} alt={`상세이미지 ${i + 1}`} key={i} />
                            ))}
                        </div>

                        {!detailsOpen && <div className="urlDetail-detailFade" aria-hidden="true" />}

                        <button
                            className="urlDetail-detailToggle"
                            onClick={() => setDetailsOpen((v) => !v)}
                            aria-expanded={detailsOpen}
                        >
                            <span className="urlDetail-toggleText">{detailsOpen ? '접기' : '상세정보 펼쳐보기'}</span>
                            <svg width="18" height="18" viewBox="0 0 24 24"
                                className={'urlDetail-toggleIcon ' + (detailsOpen ? 'is-open' : '')}
                                aria-hidden="true">
                                <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </section>

                    {/* 하단 고정 – 좋아요 | 댓글 | 선물하기 | 구매하기 */}
                    <div className="urlDetail-bottomPad" />
                    <div className="urlDetail-buybar">
                        <button className="urlDetail-iconBtn" aria-label="좋아요" onClick={() => setLiked((v) => !v)} title="좋아요">
                            <Heart style={{ fill: liked ? '#ef4444' : 'none' }} />
                        </button>

                        <button className="urlDetail-iconBtn" aria-label="댓글" title="댓글">
                            <MessageCircleMore />
                        </button>

                        <button className="urlDetail-giftBtn" onClick={() => openSheet('gift')}>
                            <FontAwesomeIcon icon={faGift} style={{ color: "#309d5c", paddingRight: "5px" }} />
                            선물하기
                        </button>

                        <button className="urlDetail-buyBtn--green" onClick={() => openSheet('buy')}>
                            구매하기
                        </button>
                    </div>

                    {/* ===== 바텀시트 모달 ===== */}
                    <div
                        className={'urlDetail-sheet ' + (sheetOpen ? 'is-open' : '')}
                        role="dialog"
                        aria-modal="true"
                        aria-label="결제 확인"
                    >
                        <div className="urlDetail-sheetDim" onClick={() => setSheetOpen(false)} />
                        <div className="urlDetail-sheetPanel">
                            <div className="urlDetail-sheetHandle" />

                            {/* 카드 헤더 */}
                            <div className="urlDetail-payCard">
                                <div className="urlDetail-payHeader">
                                    <div className="urlDetail-payText">옵션선택(필수)</div>
                                </div>
                            </div>

                            {/* 옵션 리스트: 이름 … (+/-n원) / 오른쪽에 "수량: N개" */}
                            <div className="urlDetail-optCard">
                                <div className={'optGroup is-open'}>
                                    <ul className="optList" role="listbox" aria-label="옵션 선택">
                                        {optionDefs.map((opt) => {
                                            const disabled = (optQty[opt.id] ?? 0) > 0;
                                            const suffix = priceSuffix(opt.additionalPrice);
                                            return (
                                                <li key={opt.id}>
                                                    <button
                                                        type="button"
                                                        className={'optItem ' + (disabled ? 'is-disabled' : '')}
                                                        disabled={disabled}
                                                        onClick={() => chooseOption(opt.id)}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            width: '100%',
                                                            gap: 12,
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                                                            <span className="optCheck" aria-hidden="true" />
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
                                                                <span
                                                                    className="optLabel"
                                                                    title={opt.name}
                                                                    style={{
                                                                        minWidth: 0,
                                                                        flex: 1,
                                                                        overflow: 'hidden',
                                                                        whiteSpace: 'nowrap',
                                                                        textOverflow: 'ellipsis',
                                                                        textAlign: 'left'
                                                                    }}
                                                                >
                                                                    {opt.name}
                                                                </span>
                                                                {suffix && (
                                                                    <span
                                                                        className="optSuffix"
                                                                        style={{ flex: 'none', whiteSpace: 'nowrap', color: '#666' }}
                                                                    >
                                                                        {suffix}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <span
                                                            className="optStock"
                                                            style={{ flex: 'none', whiteSpace: 'nowrap', color: '#888', fontSize: 12 }}
                                                            title={`재고 ${opt.stockQuantity ?? 0}개`}
                                                        >
                                                            재고: {opt.stockQuantity ?? 0}개
                                                        </span>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>

                                {/* 선택된 옵션 목록 */}
                                {selections.length > 0 && (
                                    <div className="urlDetail-selList">
                                        {selections.map((item) => (
                                            <div className="urlDetail-selItem" key={item.id}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                                    <div className="selLabel">{item.label}</div>
                                                    <button
                                                        type="button"
                                                        className="selRemove"
                                                        onClick={() => removeItem(item.id)}
                                                        aria-label="옵션 삭제"
                                                    >
                                                        ×
                                                    </button>
                                                </div>

                                                <div className="selRight">
                                                    <div className="urlDetail-stepper urlDetail-stepper--sm">
                                                        <button
                                                            className="urlDetail-stepBtn"
                                                            onClick={() => decQtyItem(item.id)}
                                                            disabled={item.qty <= 1}
                                                            aria-label="감소"
                                                            type="button"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24">
                                                                <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                            </svg>
                                                        </button>
                                                        <div className="urlDetail-stepValue urlDetail-stepValue--sm">{item.qty}</div>
                                                        <button
                                                            className="urlDetail-stepBtn"
                                                            onClick={() => incQtyItem(item.id)}
                                                            disabled={item.qty >= 99}
                                                            aria-label="증가"
                                                            type="button"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24">
                                                                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                            </svg>
                                                        </button>
                                                    </div>

                                                    <div className="selPrice">{fmt(unitPrice * item.qty)}원</div>
                                                </div>
                                            </div>
                                        ))}

                                        <div className="urlDetail-sheetSummary">
                                            <div>총 수량 <b className="urlDetail-accent">{totalQty}</b>개</div>
                                            <div>총 금액 <b className="urlDetail-accent">{fmt(totalAmount)}</b>원</div>
                                        </div>
                                    </div>
                                )}

                                <div className="urlDetail-shipInfoRow">
                                    <span className="shipHead">배송정보</span>
                                    <span>택배</span>
                                    <span className="shipDot">·</span>
                                    <span>{product.freeShipping ? '배송비 무료' : `배송비 ${fmt(product.shippingFee)}원`}</span>
                                </div>
                            </div>

                            <div className="urlDetail-sheetBuybar">
                                <button
                                    className="urlDetail-giftBtn"
                                    disabled={totalQty <= 0}
                                    onClick={() => handleConfirm('gift')}
                                >
                                    선물하기
                                </button>
                                <button
                                    className="urlDetail-buyBtn--green"
                                    disabled={totalQty <= 0}
                                    onClick={() => handleConfirm('buy')}
                                >
                                    구매하기
                                </button>
                            </div>
                        </div>
                    </div>
                    {/* ===== 바텀시트 끝 ===== */}
                </div>
            </div>
        </>
    );
}

export default UrlDetail;
